import { useMonitoringIntegration } from '../context/monitoringIntegration.context';
import { MIN_SEARCH_LENGTH } from '../constants/assignDriver.constants';
import { getScopeErrorMessage } from '../utils/getScopeErrorMessage';
import useNotification from '@/hooks/useNotification';
import { getErrorMessage } from '@/utils/errorUtils';
import { useState, useEffect } from 'react';
import {
  useAssignDriverMutation,
  useConfirmRouteMutation,
  useReassignDriverMutation,
} from '../services';
import {
  executeAssignDriver,
  executeReassignDriver,
} from '../utils/assignDriverRunner';

const resolveAssignDriverErrorMessage = (err: unknown, fallback: string) =>
  getScopeErrorMessage(err) ?? getErrorMessage(err, fallback);

export interface UseAssignDriverParams {
  open: boolean;
  routeId: string;
  logisticOperatorId: string;
  onClose: () => void;
  onSuccess?: () => void;
  isReassignment?: boolean;
}

export function useAssignDriver({
  open,
  routeId,
  logisticOperatorId,
  onClose,
  onSuccess,
  isReassignment = false,
}: UseAssignDriverParams) {
  const { showNotification } = useNotification();
  const [searchName, setSearchName] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const { useDriversSearch } = useMonitoringIntegration();

  const [assignDriver, { isLoading: isSubmitting }] = useAssignDriverMutation();
  const [confirmRoute] = useConfirmRouteMutation();
  const [reassignDriver, { isLoading: isReassigning }] =
    useReassignDriverMutation();

  const hasSearched = searchName.trim().length >= MIN_SEARCH_LENGTH;

  const {
    drivers,
    isLoading: isLoadingDrivers,
    isError: isDriversError,
  } = useDriversSearch({
    search: searchName,
    enabled: open,
    logisticOperatorId,
  });

  const [isReassignmentOnOpen, setIsReassignmentOnOpen] =
    useState(isReassignment);

  useEffect(() => {
    if (open) {
      setIsReassignmentOnOpen(isReassignment);
      return;
    }
    setSearchName('');
    setSelectedDriverId(null);
  }, [open]);

  const handleAssign = async () => {
    if (!selectedDriverId) return;
    const payload = { routeId, driverId: selectedDriverId, logisticOperatorId };
    const onDone = () => {
      onClose();
      onSuccess?.();
    };
    if (isReassignmentOnOpen) {
      await executeReassignDriver({
        payload,
        reassignDriver: (p) => reassignDriver(p).unwrap(),
        notify: showNotification,
        getErrorMessage: resolveAssignDriverErrorMessage,
        onSuccess: onDone,
      });
      return;
    }
    await executeAssignDriver({
      payload,
      assignDriver: (p) => assignDriver(p).unwrap(),
      confirmRoute: (p) => confirmRoute(p).unwrap(),
      notify: showNotification,
      getErrorMessage: resolveAssignDriverErrorMessage,
      onSuccess: onDone,
    });
  };

  const isBusy = isSubmitting || isReassigning;

  const handleClose = () => {
    if (!isBusy) onClose();
  };

  return {
    searchName,
    setSearchName,
    selectedDriverId,
    setSelectedDriverId,
    isLoadingDrivers,
    isDriversError,
    drivers,
    hasDrivers: drivers.length > 0,
    hasSearched,
    isSubmitting: isBusy,
    isReassignment: isReassignmentOnOpen,
    handleAssign,
    handleClose,
  };
}
